import './AllSites.css';
import React, { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import fetchBrandStores, { BrandStore, calculateStoreRecommendations, fetchStoreNotes } from '../../services/brandSitesService';
import { updateStoreNamesWithIds, MatchAddressesResult } from '../../services/storeIdService';
import Spinner from '../Spinner/Spinner';
import { HiChevronLeft, HiChevronRight, HiChevronDoubleLeft, HiChevronDoubleRight, HiOutlineFilter, HiOutlinePencilAlt, HiOutlineOfficeBuilding, HiX, HiOutlineBookmark, HiBookmark } from 'react-icons/hi';
import CustomDropdown from '../CustomDropdown/CustomDropdown';
import { HiOutlineSun, HiOutlineListBullet, HiOutlineMapPin, HiChevronUpDown, HiChevronUp, HiChevronDown, HiOutlineArrowUpTray } from 'react-icons/hi2';
import { BsSnow } from 'react-icons/bs';
import { HiOutlineMagnifyingGlass } from 'react-icons/hi2';
import { GoogleMapView, MapStore } from '../GoogleMapView';
import HeatmapToggle from '../HeatmapToggle/HeatmapToggle';
import StreetViewPanel from '../../pages/opportunityByRegion/StreetViewPanel';
import { useConfigurationStore, useExportStore, selectStoresForBrand } from '../../stores';
import CSVUploadModal from '../CSVUploadModal/CSVUploadModal';
import SitesFilters, { SitesFilterParams } from '../SitesFilters/SitesFilters';
import BrandNotesDialog from '../BrandsTable/BrandNotesDialog';
import StoreDetailModal, { StoreDetailInfo } from '../StoreDetailModal/StoreDetailModal';
import AddToListDialog from '../AddToListDialog/AddToListDialog';
import { fetchSavedStorePoiIds } from '../../services/listService';
import Tooltip from '../Tooltip/Tooltip';
import { getTooltipText } from '../../utils/tooltip';
import { trackStoreEvent } from '../../utils/posthogUtils';

interface AllSitesProps {
  brandName?: string;
  isUltimateParent?: boolean;
  stores?: any[];
  crmStoreIds?: Set<string>;
  hideExportedStores?: boolean;
  onRecommendedCountChange?: (count: number) => void;
  onSelectedStoresChange?: (selectedPoiIds: Set<string>) => void;
  onProcessedStoresChange?: (processedStores: any[]) => void;
  onSortByChange?: (sortBy: string) => void;
  onExport?: () => void;
  onDownloadXlsx?: () => void;
  onExportCrm?: () => void;
  onSaveStoresToList?: () => void;
  onImport?: () => void;
}

interface Store {
  id: number;
  poiId?: string;
  name: string;
  storeId?: string;
  location?: string;
  city?: string;
  county?: string;
  state?: string;
  zipCode?: string | number;
  status?: 'Customer' | 'Non-Customer';
  customerId?: string | number | null;
  channel?: string;
  solutions?: string;
  whitespace?: string[];
  opportunity?: string;
  crimeIndex?: { robbery: number; larceny: number };
  crimeRate?: string;
  crimeIncidents?: number;
  gkap?: string;
  impact?: string;
  highCrime?: boolean;
  highPriority?: boolean;
  crimeMessage?: string;
  suggestion?: string;
  annualRevenue?: string;
  lifetimeValue?: number;
  hasPurchekWhitespace?: boolean;
  hasContainmentWhitespace?: boolean;
  hasServiceWhitespace?: boolean;
  isDormant?: boolean;
  requiresCompliance?: boolean;
  isAcquired?: boolean;
  hasCartPenalty?: boolean;
  cartPenaltyAmount?: string;
  lawSummary?: string;
  lawSource?: string;
  lat?: number | null;
  lng?: number | null;
  recommendations?: {
    purchek: string[];
    containment: string[];
    cartManager: string[];
    subscription: string[];
    pairedWheel: string[];
    purchekBadge: { matches: boolean; tooltip: string };
    cartManagerBadge: { matches: boolean; tooltip: string };
    containmentBadge: { matches: boolean; tooltip: string };
    subscriptionBadge: { matches: boolean; tooltip: string };
    pairedWheelBadge: { matches: boolean; tooltip: string };
  };
  // Raw metrics for condition matching
  purchekCustomerCount?: number;
  purchekPenetration?: number;
  estimatedCarts?: number;
  hotDays?: number;
  coldDays?: number;
  containmentCustomerCount?: number;
  containmentPenetration?: number;
  pairedWheelPenetration?: number;
  singleWheelReplacementRate?: number;
  serviceFrequencyDays?: number;
  plaza_cm_customer_count?: number | null;
  brandName?: string;
  shopliftingAmountUsd?: number | null;
  shopliftingIncidentCount?: number | null;
  totalCrimeAmountUsd?: number | null;
  videoIncidentCount?: number | null;
  highwayProximity?: string | null;
  totalDensity?: number | null;
  firstPurchaseDate?: string | null;
  lastPurchaseDate?: string | null;
  // Rank fields
  rank?: number | null;
  cartManagerRank?: number | null;
  purchekRank?: number | null;
  containmentRank?: number | null;
  // Store notes
  store_notes?: Array<{
    id: string;
    comment: string;
    created_at?: string;
    updated_at?: string;
    user?: {
      id: string;
      name: string;
    };
  }> | null;
}

type FilterType = 'all-stores' | 'any-opportunity' | 'purchek' | 'containment' | 'cart-manager' | 'subscription' | 'paired-wheel' | 'high-crime' | 'critical-crime' | 'cart-ordinance' | 'extreme-weather';

function AllSites({ brandName, isUltimateParent, stores: propStores, crmStoreIds, hideExportedStores, onRecommendedCountChange, onSelectedStoresChange, onProcessedStoresChange, onSortByChange, onExport, onDownloadXlsx, onExportCrm, onSaveStoresToList, onImport }: AllSitesProps) {
  const { purchek, cartManager, containment, subscription, pairedWheel } = useConfigurationStore();
  const [expandedRow, setExpandedRow] = useState<number | null>(null);
  const [expandedReasons, setExpandedReasons] = useState<Set<string>>(new Set());
  const [stores, setStores] = useState<Store[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [activeFilters, setActiveFilters] = useState<Set<FilterType>>(new Set<FilterType>());
  const [filtersInitialized, setFiltersInitialized] = useState(false);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [showCSVModal, setShowCSVModal] = useState<boolean>(false);
  const [idsApplied, setIdsApplied] = useState<boolean>(false);
  const [csvUpdatedStoreIds, setCsvUpdatedStoreIds] = useState<Set<string>>(new Set());
  const [publishing, setPublishing] = useState<boolean>(false);
  const [publishCompleted, setPublishCompleted] = useState<boolean>(false);
  const [expandedSolutions, setExpandedSolutions] = useState<Set<number>>(new Set());
  const [expandedWhitespace, setExpandedWhitespace] = useState<Set<number>>(new Set());
  const [columnSortBy, setColumnSortBy] = useState<string | null>(null);
  const [columnSortOrder, setColumnSortOrder] = useState<'asc' | 'desc'>('asc');
  const [isSitesFilterOpen, setIsSitesFilterOpen] = useState(false);
  const [appliedFilters, setAppliedFilters] = useState<SitesFilterParams>({});
  const [notesDialogOpen, setNotesDialogOpen] = useState(false);
  const [selectedStoreForNotes, setSelectedStoreForNotes] = useState('');
  const [selectedStorePoiId, setSelectedStorePoiId] = useState('');
  const [storeDetailModalOpen, setStoreDetailModalOpen] = useState(false);
  const [selectedStoreDetail, setSelectedStoreDetail] = useState<StoreDetailInfo | null>(null);
  const [listDialogOpen, setListDialogOpen] = useState(false);
  const [selectedStoreForList, setSelectedStoreForList] = useState<{ poiId: string; storeName: string; brandName: string }>({ poiId: '', storeName: '', brandName: '' });
  const [bulkStoresForList, setBulkStoresForList] = useState<{ poiId: string; storeName?: string; brandName?: string }[]>([]);
  const [savedStores, setSavedStores] = useState<Set<string>>(new Set());
  const brandKey = brandName ? decodeURIComponent(brandName).toUpperCase() : '';
  const selectedStoreIds = useExportStore(selectStoresForBrand(brandKey));
  const toggleStoreInExportStore = useExportStore((s) => s.toggleStore);
  const setStoresInExportStore = useExportStore((s) => s.setSelectedStores);
  const [viewMode, setViewMode] = useState<'list' | 'map'>('list');
  const [showTop20, setShowTop20] = useState(false);
  const [showHeatmap, setShowHeatmap] = useState(false);
  const [streetViewStore, setStreetViewStore] = useState<{ lat: number; lng: number; name?: string } | null>(null);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [showMoreShortcuts, setShowMoreShortcuts] = useState(false);

  // Sync Zustand export store to parent on mount (picks up selections from StoresMapPage)
  useEffect(() => {
    if (selectedStoreIds.size > 0) {
      onSelectedStoresChange?.(selectedStoreIds);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load saved store POI IDs for showing "Saved" state
  const loadSavedStores = useCallback(async () => {
    const ids = await fetchSavedStorePoiIds();
    setSavedStores(ids);
  }, []);

  useEffect(() => {
    loadSavedStores();
  }, [loadSavedStores]);

  // Track store search in PostHog (debounced)
  useEffect(() => {
    if (!searchQuery.trim()) return;
    const timer = setTimeout(() => {
      trackStoreEvent.searchStores(searchQuery.trim());
    }, 500);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const handleNotesClick = (storeName: string, poiId?: string) => {
    setSelectedStoreForNotes(storeName);
    setSelectedStorePoiId(poiId || '');
    setNotesDialogOpen(true);
  };

  const handleViewDetails = (store: Store) => {
    const storeDetail: StoreDetailInfo = {
      id: store.id,
      poiId: store.poiId,
      site: store.storeId ? `${store.brandName || store.name} ${store.storeId}` : store.name || store.brandName,
      storeId: store.storeId,
      address: store.location,
      city: store.city,
      state: store.state,
      zipCode: store.zipCode?.toString(),
      status: store.status,
      ultimateParent: brandName,
      channel: store.channel?.toLowerCase() === 'diy' ? 'DIY' : (store.channel || 'Unknown'),
      crimeRate: store.crimeRate,
      gkap: store.gkap,
      solutions: store.solutions,
      whitespace: store.whitespace,
      lifetimeValue: store.lifetimeValue,
      hasPurchek: (store.recommendations?.purchek?.length ?? 0) > 0,
      hasContainment: (store.recommendations?.containment?.length ?? 0) > 0,
      hasCartManager: (store.recommendations?.cartManager?.length ?? 0) > 0,
      hasSubscription: (store.recommendations?.subscription?.length ?? 0) > 0,
      hasPairedWheel: (store.recommendations?.pairedWheel?.length ?? 0) > 0,
      crimeIncidents: store.crimeIncidents,
      shopliftingAmountUsd: store.shopliftingAmountUsd,
      shopliftingIncidentCount: store.shopliftingIncidentCount,
      totalCrimeAmountUsd: store.totalCrimeAmountUsd,
      videoIncidentCount: store.videoIncidentCount,
      firstPurchaseDate: store.firstPurchaseDate,
      lastPurchaseDate: store.lastPurchaseDate,
      // Additional fields (dummy values for now)
      sitesAcquired: 12,
      upsellOpportunity: 'High',
      winBack: 'Medium',
      subscriptionSites: 8,
      cartOrdinance: store.hasCartPenalty ?? null,
      storeDensity: store.totalDensity != null
        ? (store.totalDensity >= 10 ? 'High' : store.totalDensity >= 5 ? 'Medium' : 'Low')
        : null,
      // Neighbourhood & Environment
      highwayProximity: store.highwayProximity ?? null,
      storesWithin5mi: store.totalDensity ?? null,
      heatDays: store.hotDays ?? null,
      coldDays: store.coldDays ?? null,
      busStopProximity: null,
      lat: store.lat ?? null,
      lng: store.lng ?? null,
    };
    setSelectedStoreDetail(storeDetail);
    setStoreDetailModalOpen(true);
  };

  const handleAddToListClick = (store: Store) => {
    if (selectedStoreIds.size > 0 && selectedStoreIds.has(store.poiId || '')) {
      // When the clicked store is part of a selection, save all selected stores
      const bulk = filteredStores
        .filter(s => selectedStoreIds.has(s.poiId || ''))
        .map(s => ({ poiId: s.poiId || '', storeName: s.storeId ? `${s.brandName || s.name} ${s.storeId}` : s.name || s.brandName, brandName: s.brandName || brandName || '' }));
      setBulkStoresForList(bulk);
      setSelectedStoreForList({ poiId: '', storeName: '', brandName: '' });
    } else {
      // Single store save (no selection or clicked store is not selected)
      setBulkStoresForList([]);
      setSelectedStoreForList({
        poiId: store.poiId || '',
        storeName: store.storeId ? `${store.brandName || store.name || ''} ${store.storeId}` : store.name || store.brandName || '',
        brandName: store.brandName || brandName || ''
      });
    }
    setListDialogOpen(true);
  };

  const handleNoteAdded = () => {
    // Only update the notes for the specific store instead of refetching all stores
    if (!selectedStorePoiId) return;
    const poiId = selectedStorePoiId;
    const load = async () => {
      const notes = await fetchStoreNotes(poiId);
      setStores(prev => prev.map(store =>
        store.poiId === poiId ? { ...store, store_notes: notes } : store
      ));
    };
    load();
  };

  // Get active readiness levels and their conditions for all solutions
  const purchekActiveReadinessId = purchek.activeReadiness[0];
  const purchekActiveReadinessLevel = purchek.readinessLevels.find(level => level.id === purchekActiveReadinessId);
  const purchekActiveConditions = useMemo(() => purchekActiveReadinessLevel ? purchek.conditions.reduce((acc, cond) => {
    acc[cond.metric] = (cond as any)[purchekActiveReadinessId];
    return acc;
  }, {} as Record<string, string>) : {}, [purchekActiveReadinessLevel, purchekActiveReadinessId, purchek.conditions]);

  const cartManagerActiveReadinessId = cartManager.activeReadiness[0];
  const cartManagerActiveReadinessLevel = cartManager.readinessLevels.find(level => level.id === cartManagerActiveReadinessId);
  const cartManagerActiveConditions = useMemo(() => cartManagerActiveReadinessLevel ? cartManager.conditions.reduce((acc, cond) => {
    acc[cond.metric] = (cond as any)[cartManagerActiveReadinessId];
    return acc;
  }, {} as Record<string, string>) : {}, [cartManagerActiveReadinessLevel, cartManagerActiveReadinessId, cartManager.conditions]);

  const containmentActiveReadinessId = containment.activeReadiness[0];
  const containmentActiveReadinessLevel = containment.readinessLevels.find(level => level.id === containmentActiveReadinessId);
  const containmentActiveConditions = useMemo(() => containmentActiveReadinessLevel ? containment.conditions.reduce((acc, cond) => {
    acc[cond.metric] = (cond as any)[containmentActiveReadinessId];
    return acc;
  }, {} as Record<string, string>) : {}, [containmentActiveReadinessLevel, containmentActiveReadinessId, containment.conditions]);

  const subscriptionActiveReadinessId = subscription.activeReadiness[0];
  const subscriptionActiveReadinessLevel = subscription.readinessLevels.find(level => level.id === subscriptionActiveReadinessId);
  const subscriptionActiveConditions = useMemo(() => subscriptionActiveReadinessLevel ? subscription.conditions.reduce((acc, cond) => {
    acc[cond.metric] = (cond as any)[subscriptionActiveReadinessId];
    return acc;
  }, {} as Record<string, string>) : {}, [subscriptionActiveReadinessLevel, subscriptionActiveReadinessId, subscription.conditions]);

  const pairedWheelActiveReadinessId = pairedWheel.activeReadiness[0];
  const pairedWheelActiveReadinessLevel = pairedWheel.readinessLevels.find(level => level.id === pairedWheelActiveReadinessId);
  const pairedWheelActiveConditions = useMemo(() => pairedWheelActiveReadinessLevel ? pairedWheel.conditions.reduce((acc, cond) => {
    acc[cond.metric] = (cond as any)[pairedWheelActiveReadinessId];
    return acc;
  }, {} as Record<string, string>) : {}, [pairedWheelActiveReadinessLevel, pairedWheelActiveReadinessId, pairedWheel.conditions]);

  // Keep backward compatibility aliases
  const activeReadinessId = purchekActiveReadinessId;
  const activeReadinessLevel = purchekActiveReadinessLevel;

  useEffect(() => {
    const load = async () => {
      setLoading(true);

      let brandStores: BrandStore[] = [];

      if (isUltimateParent && propStores) {
        // Use the aggregated stores passed from OpportunityDetail
        brandStores = propStores;
      } else {
        const brandNameForQuery = brandName ? decodeURIComponent(brandName).toUpperCase() : 'SAVE-A-LOT';
        brandStores = await fetchBrandStores(brandNameForQuery);
        console.debug('AllSites: fetched brandStores', brandStores);
      }

      // Get estimated carts from the first store's brand data
      const estimatedCartsPerStore = brandStores.length > 0 ? (brandStores[0].estimated_no_of_carts_per_store || 0) : 0;
      console.debug('AllSites: estimatedCartsPerStore', estimatedCartsPerStore);

      const mapped: Store[] = brandStores.map((s, idx) => {
        const solutionsArr: string[] = [];
        const whitespaceArr: string[] = [];

        // Check all solutions
        if (s.purchek_install_date) solutionsArr.push('Purchek');
        else whitespaceArr.push('Purchek');

        if (s.containment_install_date) solutionsArr.push('Cart Containment');
        else whitespaceArr.push('Cart Containment');

        if (s.cart_manager_install_date) solutionsArr.push('Cart Manager');
        else whitespaceArr.push('Cart Manager');

        if (s.subscription_start_date) solutionsArr.push('Subscription');
        else whitespaceArr.push('Subscription');

        if (s.paired_wheel_install_date) solutionsArr.push('Paired Wheel');
        else whitespaceArr.push('Paired Wheel');

        const solutionsStr = solutionsArr.length > 0 ? solutionsArr.join(', ') : 'None';

        // Determine crime rate from geo data
        const crimeRateStr = s.geo?.crime_rate ?? '-';
        const crimeIncidents = s.geo?.total_crime_incidents ?? 0;
        const avgCrime = 0; // crime_rate is a categorical string, not a number
        const highCrime = crimeIncidents >= 1000;

        // Calculate filter properties
        const hasPurchekWhitespace = whitespaceArr.includes('Purchek');
        const hasContainmentWhitespace = whitespaceArr.includes('Cart Containment');
        const hasServiceWhitespace = whitespaceArr.includes('Cart Manager') || whitespaceArr.includes('Paired Wheel');
        // Dormant = has at least one solution installed, but the most recent install/purchase date is > 3 years ago
        const installDates = [
          s.purchek_install_date,
          s.containment_install_date,
          s.cart_manager_install_date,
          s.subscription_start_date,
          s.paired_wheel_install_date,
          s.last_purchase,
        ]
          .filter((d): d is string => Boolean(d))
          .map((d) => new Date(d).getTime())
          .filter((t: number) => !isNaN(t));
        const latestDate = installDates.length > 0 ? Math.max(...installDates) : null;
        const threeYearsAgo = Date.now() - 3 * 365.25 * 24 * 60 * 60 * 1000;
        const isDormant = solutionsArr.length > 0 && latestDate !== null && latestDate < threeYearsAgo;
        const hasCartPenalty = !!s.geo?.penalty_for_cart_owner;

        // Calculate recommendations for this store
        const recommendations = calculateStoreRecommendations(
          s,
          estimatedCartsPerStore,
          purchekActiveConditions,
          cartManagerActiveConditions,
          containmentActiveConditions,
          subscriptionActiveConditions,
          pairedWheelActiveConditions,
          purchekActiveReadinessLevel?.name,
          cartManagerActiveReadinessLevel?.name,
          containmentActiveReadinessLevel?.name,
          subscriptionActiveReadinessLevel?.name,
          pairedWheelActiveReadinessLevel?.name
        );

        return {
          id: idx + 1,
          poiId: s.id || `poi-${idx}`,
          name: s.site,
          storeId: s.updated_store_ids?.[0]?.store_id || '',
          location: s.address ?? '',
          city: s.geo?.city ? s.geo.city.charAt(0).toUpperCase() + s.geo.city.slice(1).toLowerCase() : '-',
          county: s.geo?.county ? s.geo.county.charAt(0).toUpperCase() + s.geo.county.slice(1).toLowerCase() : '-',
          state: s.geo?.state ? s.geo.state.charAt(0).toUpperCase() + s.geo.state.slice(1).toLowerCase() : '-',
          zipCode: s.zipcode ?? '',
          status: s.customer_id != null ? 'Customer' : 'Non-Customer',
          customerId: s.customer_id ?? null,
          channel: s.channel?.toLowerCase() === 'diy' ? 'DIY' : (s.channel ?? 'Unknown'),
          solutions: solutionsStr,
          whitespace: whitespaceArr,
          opportunity: whitespaceArr.length > 0 ? whitespaceArr.join(', ') : 'None',
          crimeIndex: { robbery: avgCrime, larceny: 0 },
          crimeRate: crimeRateStr,
          crimeIncidents,
          gkap: s.nearby_penetration?.penetration ? `${parseFloat(String(s.nearby_penetration.penetration)).toFixed(2)}%` : '-',
          impact: '-',
          annualRevenue: '-',
          lifetimeValue: s.lifetime_value ?? undefined,
          highCrime: highCrime,
          crimeMessage: `Crime Rate: ${crimeRateStr}`,
          hasPurchekWhitespace,
          hasContainmentWhitespace,
          hasServiceWhitespace,
          isDormant,
          requiresCompliance: false,
          isAcquired: s.customer_id != null,
          hasCartPenalty,
          cartPenaltyAmount: '$250',
          lawSummary: s.geo?.law_summary ?? '',
          lawSource: s.geo?.law_source ?? '',
          recommendations,
          lat: (s as any).latitude ?? null,
          lng: (s as any).longitude ?? null,
          // Raw metrics for condition matching
          purchekCustomerCount: s.nearby_penetration?.purchek_customer_count ?? 0,
          purchekPenetration: s.nearby_penetration?.penetration_purchek ?? 0,
          containmentCustomerCount: s.nearby_penetration?.containment_customer_count ?? 0,
          containmentPenetration: s.nearby_penetration?.penetration_containment ?? 0,
          pairedWheelPenetration: (s as any).pairedWheelPenetration ?? s.nearby_penetration?.penetration_paired_wheel ?? 0,
          singleWheelReplacementRate: (s as any).singleWheelReplacementRate ?? s.wheel?.single_wheel_replacement_rate ?? 0,
          serviceFrequencyDays: (s as any).serviceFrequency ?? s.wheel?.service_frequency ?? 0,
          plaza_cm_customer_count: s.nearby_penetration?.plaza_cm_customer_count ?? null,
          estimatedCarts: estimatedCartsPerStore || 0,
          hotDays: s.geo?.no_of_hot_days ?? 0,
          coldDays: s.geo?.no_of_cold_days ?? 0,
          brandName: (s as any).brand_name ?? undefined,
          store_notes: s.store_notes,
          shopliftingAmountUsd: s.nearby_penetration?.shoplifting_amount_usd ?? null,
          shopliftingIncidentCount: s.nearby_penetration?.shoplifting_incident_count ?? null,
          totalCrimeAmountUsd: s.video?.total_crime_amount_usd ?? null,
          videoIncidentCount: s.video?.incident_count ?? null,
          highwayProximity: (s as any).highwayProximity ?? s.nearby_penetration?.highway_proximity ?? null,
          totalDensity: (s as any).totalDensity ?? s.nearby_penetration?.total_density ?? null,
          firstPurchaseDate: s.first_purchase ?? null,
          lastPurchaseDate: s.last_purchase ?? null,
          rank: (s as any).rank ?? null,
          cartManagerRank: (s as any).cart_manager_rank ?? null,
          purchekRank: (s as any).purchek_rank ?? null,
          containmentRank: (s as any).containment_rank ?? null
        };
      });
      setStores(mapped);
      console.debug('AllSites: mapped stores', mapped);
      const customersCount = mapped.filter((m) => m.status === 'Customer').length;
      const nonCustomersCount = mapped.length - customersCount;
      console.debug('AllSites: counts', { total: mapped.length, customersCount, nonCustomersCount });
      setLoading(false);
    };
    load();
  }, [
    brandName,
    isUltimateParent,
    propStores,
    purchekActiveReadinessId,
    cartManagerActiveReadinessId,
    containmentActiveReadinessId,
    subscriptionActiveReadinessId,
    pairedWheelActiveReadinessId,
    purchekActiveConditions,
    cartManagerActiveConditions,
    containmentActiveConditions,
    subscriptionActiveConditions,
    pairedWheelActiveConditions,
    purchekActiveReadinessLevel?.name,
    cartManagerActiveReadinessLevel?.name,
    containmentActiveReadinessLevel?.name,
    subscriptionActiveReadinessLevel?.name,
    pairedWheelActiveReadinessLevel?.name
  ]);

  useEffect(() => {
    if (!filtersInitialized && stores.length > 0) {
      if (stores.length >= 11) {
        setActiveFilters(new Set<FilterType>(['purchek', 'containment']));
      }
      setFiltersInitialized(true);
    }
  }, [stores, filtersInitialized]);

  const toggleRow = (id: number) => {
    setExpandedRow(expandedRow === id ? null : id);
  };

  const getCrimeIndex = (crimeIncidents: number | undefined): string => {
    if (!crimeIncidents) return 'No Data';
    if (crimeIncidents >= 5000) return 'Critical';
    if (crimeIncidents >= 1000) return 'High';
    if (crimeIncidents >= 200) return 'Medium';
    if (crimeIncidents > 0) return 'Low';
    return 'No Data';
  };

  const getStoreDensityLabel = (density: number | null | undefined): string => {
    if (density == null || density === 0) return 'None';
    if (density >= 10) return 'High';
    if (density >= 5) return 'Medium';
    return 'Low';
  };

  const getHighwayProximityLabel = (proximity: string | null | undefined): string => {
    if (!proximity) return 'No Data';
    const miles = parseFloat(proximity);
    if (isNaN(miles)) return proximity;
    if (miles <= 1) return 'Very Close';
    if (miles <= 5) return 'Near';
    if (miles <= 15) return 'Moderate';
    return 'Far';
  };

  // const getCrimeIndexColor = (crimeIndex: string): string => {
  //   switch (crimeIndex) {
  //     case 'Critical':
  //       return '#991b1b';
  //     case 'High':
  //       return '#dc2626';
  //     case 'Medium':
  //       return '#f59e0b';
  //     case 'Low':
  //       return '#10b981';
  //     default:
  //       return '#6b7280';
  //   }
  // };

  const handleColumnSort = (column: string) => {
    if (columnSortBy === column) {
      if (columnSortOrder === 'asc') {
        setColumnSortOrder('desc');
      } else {
        // Third click: reset to rank-based sorting
        setColumnSortBy(null);
        setColumnSortOrder('asc');
      }
    } else {
      setColumnSortBy(column);
      setColumnSortOrder('asc');
    }
  };

  const getAvailableStates = (storesList: Store[]): string[] => {
    const states = storesList
      .map(store => store.state)
      .filter((state): state is string => typeof state === 'string' && state !== '-' && state !== '')
      .sort((a, b) => a.localeCompare(b));
    return Array.from(new Set(states));
  };

  const getAvailableCounties = (storesList: Store[]): string[] => {
    const counties = storesList
      .map(store => store.county)
      .filter((county): county is string => typeof county === 'string' && county !== '-' && county !== '')
      .sort((a, b) => a.localeCompare(b));
    return Array.from(new Set(counties));
  };

  const getAvailableCities = (storesList: Store[]): string[] => {
    const cities = storesList
      .map(store => store.city)
      .filter((city): city is string => typeof city === 'string' && city !== '-' && city !== '')
      .sort((a, b) => a.localeCompare(b));
    return Array.from(new Set(cities));
  };

  const getAvailableSubBrands = (storesList: Store[]): string[] => {
    return Array.from(new Set(storesList.map(s => s.brandName).filter((b): b is string => !!b))).sort();
  };

  const handleCSVImport = (result: MatchAddressesResult) => {
    const updatedIds = new Set<string>();

    // Build a lookup: s_id (poi_id) -> c_id (client store ID)
    const matchMap = new Map<string, string>();
    result.matches.forEach(m => {
      matchMap.set(m.s_id, m.c_id);
    });

    const updatedStores = stores.map(store => {
      const clientId = matchMap.get(store.poiId || '');
      if (clientId) {
        updatedIds.add(store.poiId || '');
        return {
          ...store,
          storeId: clientId
        };
      }
      return store;
    });

    setStores(updatedStores);
    setCsvUpdatedStoreIds(updatedIds);
    setIdsApplied(true);
    setPublishCompleted(false);

  };

  const handlePublishToSheet = async () => {
    setPublishing(true);
    try {
      const storeUpdates = stores
        .filter(store => csvUpdatedStoreIds.has(store.poiId || ''))
        .map(store => ({
          poiId: store.poiId as string,
          storeId: store.storeId || '',
          address: store.location || '',
          zipcode: store.zipCode?.toString() || '',
          brand_name: brandName || ''
        }))
        .filter(update => update.poiId && update.storeId);

      const result = await updateStoreNamesWithIds(storeUpdates);
      alert(result.message);
      if (result.success) {
        setPublishCompleted(true);
      }
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : 'Unknown error occurred';
      alert(`Failed to publish: ${errorMsg}`);
      console.error('Publish error:', error);
    } finally {
      setPublishing(false);
    }
  };

  // Calculate dynamic shortcut counts
  const anyOpportunityCount = stores.filter(s => s.recommendations && (s.recommendations.purchek.length > 0 || s.recommendations.containment.length > 0 || s.recommendations.cartManager.length > 0 || s.recommendations.subscription.length > 0 || s.recommendations.pairedWheel.length > 0)).length;
  const purchekCount = stores.filter(s => s.recommendations && s.recommendations.purchek.length > 0).length;
  const containmentCount = stores.filter(s => s.recommendations && s.recommendations.containment.length > 0).length;
  const cartManagerCount = stores.filter(s => s.recommendations && s.recommendations.cartManager.length > 0).length;
  const subscriptionCount = stores.filter(s => s.recommendations && s.recommendations.subscription.length > 0).length;
  const pairedWheelCount = stores.filter(s => s.recommendations && s.recommendations.pairedWheel.length > 0).length;
  const highCrimeCount = stores.filter(s => (s.crimeIncidents ?? 0) >= 1000 && (s.crimeIncidents ?? 0) < 5000).length;
  const criticalCrimeCount = stores.filter(s => (s.crimeIncidents ?? 0) >= 5000).length;
  const cartOrdinanceCount = stores.filter(s => s.hasCartPenalty || s.requiresCompliance).length;
  const extremeWeatherCount = stores.filter(s => (s.hotDays ?? 0) >= 50 || (s.coldDays ?? 0) >= 50).length;
  const totalRecommendedCount = purchekCount + containmentCount + cartManagerCount + subscriptionCount + pairedWheelCount;

  useEffect(() => {
    onRecommendedCountChange?.(totalRecommendedCount);
  }, [totalRecommendedCount, onRecommendedCountChange]);

  const matchesQuickFilter = useCallback((store: Store): boolean => {
    if (activeFilters.size === 0) return true;
    return Array.from(activeFilters).every(filter => {
      switch (filter) {
        case 'any-opportunity':
          return !!(store.recommendations && (store.recommendations.purchek.length > 0 || store.recommendations.containment.length > 0 || store.recommendations.cartManager.length > 0 || store.recommendations.subscription.length > 0 || store.recommendations.pairedWheel.length > 0));
        case 'purchek':
          return (store.recommendations && store.recommendations.purchek.length > 0) ?? false;
        case 'containment':
          return (store.recommendations && store.recommendations.containment.length > 0) ?? false;
        case 'cart-manager':
          return (store.recommendations && store.recommendations.cartManager.length > 0) ?? false;
        case 'subscription':
          return (store.recommendations && store.recommendations.subscription.length > 0) ?? false;
        case 'paired-wheel':
          return (store.recommendations && store.recommendations.pairedWheel.length > 0) ?? false;
        case 'high-crime':
          return (store.crimeIncidents ?? 0) >= 1000 && (store.crimeIncidents ?? 0) < 5000;
        case 'critical-crime':
          return (store.crimeIncidents ?? 0) >= 5000;
        case 'cart-ordinance':
          return !!(store.hasCartPenalty || store.requiresCompliance);
        case 'extreme-weather':
          return (store.hotDays ?? 0) >= 50 || (store.coldDays ?? 0) >= 50;
        default:
          return true;
      }
    });
  }, [activeFilters]);

  const quickFilteredStores = useMemo(() => {
    let result = stores.filter(matchesQuickFilter);
    if (hideExportedStores && crmStoreIds && crmStoreIds.size > 0) {
      result = result.filter(store => !crmStoreIds.has(store.poiId || ''));
    }
    return result;
  }, [stores, matchesQuickFilter, hideExportedStores, crmStoreIds]);

  const filteredStores = quickFilteredStores.filter(store => {
    let matchesFilter = true;

    // Apply applied filters from the filter panel
    if (matchesFilter && appliedFilters.status && appliedFilters.status !== 'All Status') {
      matchesFilter = store.status === appliedFilters.status;
    }

    if (matchesFilter && appliedFilters.solutions) {
      const solutionsToMatch = Array.isArray(appliedFilters.solutions)
        ? appliedFilters.solutions
        : appliedFilters.solutions.split(',').map(s => s.trim());
      const storeSolutions = store.solutions ? store.solutions.split(',').map(s => s.trim()) : [];
      matchesFilter = solutionsToMatch.some(sol => storeSolutions.includes(sol));
    }

    if (matchesFilter && appliedFilters.whitespace && appliedFilters.whitespace.length > 0) {
      matchesFilter = appliedFilters.whitespace.some(ws => store.whitespace?.includes(ws)) ?? false;
    }

    if (matchesFilter && appliedFilters.crimeLevel && appliedFilters.crimeLevel.length > 0) {
      const crimeLabel = getCrimeIndex(store.crimeIncidents);
      matchesFilter = appliedFilters.crimeLevel.includes(crimeLabel);
    }

    if (matchesFilter && appliedFilters.cartPenalty) {
      if (appliedFilters.cartPenalty === 'Penalty for Cart Owner' || appliedFilters.cartPenalty === 'General Recommendation') {
        // Both map to value 1 (has penalty)
        matchesFilter = (store.hasCartPenalty ?? false) || (store.requiresCompliance ?? false);
      } else if (appliedFilters.cartPenalty === 'No Ordinance') {
        // Maps to null/0 (no penalty)
        matchesFilter = !((store.hasCartPenalty ?? false) || (store.requiresCompliance ?? false));
      }
    }

    // Apply range filters for Purchek neighborhood sites
    if (matchesFilter && appliedFilters.purchekNeighborhoodSites) {
      const purchekCount = store.purchekCustomerCount ?? 0;
      matchesFilter = purchekCount >= appliedFilters.purchekNeighborhoodSites.min &&
                      purchekCount <= appliedFilters.purchekNeighborhoodSites.max;
    }

    // Apply range filters for Purchek penetration
    if (matchesFilter && appliedFilters.purchekPenetration) {
      const penetration = store.purchekPenetration ?? 0;
      matchesFilter = penetration >= appliedFilters.purchekPenetration.min &&
                      penetration <= appliedFilters.purchekPenetration.max;
    }

    // Apply range filters for hot days
    if (matchesFilter && appliedFilters.hotDays) {
      const hotDaysCount = store.hotDays ?? 0;
      matchesFilter = hotDaysCount >= appliedFilters.hotDays.min &&
                      hotDaysCount <= appliedFilters.hotDays.max;
    }

    // Apply range filters for cold days
    if (matchesFilter && appliedFilters.coldDays) {
      const coldDaysCount = store.coldDays ?? 0;
      matchesFilter = coldDaysCount >= appliedFilters.coldDays.min &&
                      coldDaysCount <= appliedFilters.coldDays.max;
    }

    // Apply state filter
    if (matchesFilter && appliedFilters.states && appliedFilters.states.length > 0) {
      matchesFilter = appliedFilters.states.includes(store.state ?? '');
    }

    // Apply county filter
    if (matchesFilter && appliedFilters.counties && appliedFilters.counties.length > 0) {
      matchesFilter = appliedFilters.counties.includes(store.county ?? '');
    }

    // Apply city filter
    if (matchesFilter && appliedFilters.cities && appliedFilters.cities.length > 0) {
      matchesFilter = appliedFilters.cities.includes(store.city ?? '');
    }

    // Apply located in shopping complex filter
    if (matchesFilter && appliedFilters.locatedInShoppingComplex) {
      const plazaCmValue = store.plaza_cm_customer_count ?? 0;
      const isInComplex = plazaCmValue !== 0 && plazaCmValue !== null;

      if (appliedFilters.locatedInShoppingComplex === 'No') {
        matchesFilter = !isInComplex;
      } else if (appliedFilters.locatedInShoppingComplex === 'Yes, with containment store(s) in the complex') {
        matchesFilter = isInComplex;
      } else if (appliedFilters.locatedInShoppingComplex === 'Yes, without containment store(s) in the complex') {
        matchesFilter = !isInComplex;
      }
    }

    if (matchesFilter && appliedFilters.containmentNeighborhoodSites) {
      const count = store.containmentCustomerCount ?? 0;
      matchesFilter = count >= appliedFilters.containmentNeighborhoodSites.min &&
                      count <= appliedFilters.containmentNeighborhoodSites.max;
    }

    if (matchesFilter && appliedFilters.containmentPenetration) {
      const penetration = store.containmentPenetration ?? 0;
      matchesFilter = penetration >= appliedFilters.containmentPenetration.min &&
                      penetration <= appliedFilters.containmentPenetration.max;
    }

    if (matchesFilter && appliedFilters.pairedWheelPenetration) {
      const penetration = store.pairedWheelPenetration ?? 0;
      matchesFilter = penetration >= appliedFilters.pairedWheelPenetration.min &&
                      penetration <= appliedFilters.pairedWheelPenetration.max;
    }

    if (matchesFilter && appliedFilters.singleWheelReplacement) {
      const rate = store.singleWheelReplacementRate ?? 0;
      matchesFilter = rate >= appliedFilters.singleWheelReplacement.min &&
                      rate <= appliedFilters.singleWheelReplacement.max;
    }

    if (matchesFilter && appliedFilters.subBrands && appliedFilters.subBrands.length > 0) {
      matchesFilter = appliedFilters.subBrands.includes(store.brandName ?? '');
    }

    // Apply Pushout & Recovery filters
    if (matchesFilter && appliedFilters.neighbourhoodPushoutIncidents && appliedFilters.neighbourhoodPushoutIncidents.length > 0) {
      const value = store.shopliftingIncidentCount ?? 0;
      matchesFilter = appliedFilters.neighbourhoodPushoutIncidents.some(range => {
        if (range === '5000+') return value >= 5000;
        const [min, max] = range.split('-').map(Number);
        return value >= min && value < max;
      });
    }

    if (matchesFilter && appliedFilters.storePushoutIncidents && appliedFilters.storePushoutIncidents.length > 0) {
      const value = store.videoIncidentCount ?? 0;
      matchesFilter = appliedFilters.storePushoutIncidents.some(range => {
        if (range === '1000+') return value >= 1000;
        const [min, max] = range.split('-').map(Number);
        return value >= min && value < max;
      });
    }

    if (matchesFilter && appliedFilters.neighbourhoodRecoveryAmount && appliedFilters.neighbourhoodRecoveryAmount.length > 0) {
      const value = store.shopliftingAmountUsd ?? 0;
      matchesFilter = appliedFilters.neighbourhoodRecoveryAmount.some(range => {
        const cleaned = range.replace(/\$/g, '');
        if (cleaned === '100000+') return value >= 100000;
        const [min, max] = cleaned.split('-').map(Number);
        return value >= min && value < max;
      });
    }

    if (matchesFilter && appliedFilters.storeRecoveryAmount && appliedFilters.storeRecoveryAmount.length > 0) {
      const value = store.totalCrimeAmountUsd ?? 0;
      matchesFilter = appliedFilters.storeRecoveryAmount.some(range => {
        const cleaned = range.replace(/\$/g, '');
        if (cleaned === '10000+') return value >= 10000;
        const [min, max] = cleaned.split('-').map(Number);
        return value >= min && value < max;
      });
    }

    if (matchesFilter && appliedFilters.storeDensity && appliedFilters.storeDensity.length > 0) {
      const densityLabel = getStoreDensityLabel(store.totalDensity);
      matchesFilter = appliedFilters.storeDensity.includes(densityLabel);
    }
    if (matchesFilter && appliedFilters.highwayProximity && appliedFilters.highwayProximity.length > 0) {
      const proximityLabel = getHighwayProximityLabel(store.highwayProximity);
      matchesFilter = appliedFilters.highwayProximity.includes(proximityLabel);
    }

    // Apply quick filters from the filter panel
    if (matchesFilter && appliedFilters.quickFilters && appliedFilters.quickFilters.length > 0) {
      const matchesAnyQuickFilter = appliedFilters.quickFilters.some(qf => {
        switch (qf) {
          case 'high-crime':
            return (store.crimeIncidents ?? 0) >= 1000;
          case 'cart-penalty':
            return store.hasCartPenalty || store.requiresCompliance;
          case 'any-opportunity':
            return !!(store.recommendations && (store.recommendations.purchek.length > 0 || store.recommendations.containment.length > 0 || store.recommendations.cartManager.length > 0 || store.recommendations.subscription.length > 0 || store.recommendations.pairedWheel.length > 0));
          case 'dormant-stores':
            return store.isDormant;
          default:
            return true;
        }
      });
      matchesFilter = matchesAnyQuickFilter;
    }

    // Then apply search
    if (!searchQuery.trim()) {
      return matchesFilter;
    }

    const query = searchQuery.toLowerCase();
    return (
      matchesFilter &&
      (store.name?.toLowerCase().includes(query) ||
        store.location?.toLowerCase().includes(query) ||
        store.storeId?.toLowerCase().includes(query) ||
        store.city?.toLowerCase().includes(query) ||
        store.state?.toLowerCase().includes(query) ||
        String(store.zipCode).toLowerCase().includes(query))
    );
  });

  const prevFilteredKeyRef = useRef<string>('');

  const getActiveSortBy = useMemo(() => {
    const filters = Array.from(activeFilters);

    if (filters.length === 0) return 'rank';

    const hasPurchek = filters.includes('purchek');
    const hasContainment = filters.includes('containment');
    const hasCartManager = filters.includes('cart-manager');

    if (hasPurchek && hasContainment && !hasCartManager) {
      return 'rank';
    } else if (hasPurchek && !hasContainment && !hasCartManager) {
      return 'purchekRank';
    } else if (!hasPurchek && hasContainment && !hasCartManager) {
      return 'containmentRank';
    } else if (!hasPurchek && !hasContainment && hasCartManager) {
      return 'cartManagerRank';
    } else if (hasPurchek && hasCartManager && !hasContainment) {
      return 'rank';
    } else if (hasContainment && hasCartManager && !hasPurchek) {
      return 'rank';
    } else if (hasPurchek && hasContainment && hasCartManager) {
      return 'rank';
    }

    return 'rank';
  }, [activeFilters]);

  // Assign sequential ranks (1, 2, 3...) based on quick shortcut sort order (filtered stores)
  const storeSequentialRank = useMemo(() => {
    const rankMap = new Map<number, number>();
    const sorted = [...quickFilteredStores].sort((a, b) => {
      const field = getActiveSortBy;
      const aVal = field === 'purchekRank' ? a.purchekRank
        : field === 'containmentRank' ? a.containmentRank
        : field === 'cartManagerRank' ? a.cartManagerRank
        : a.rank;
      const bVal = field === 'purchekRank' ? b.purchekRank
        : field === 'containmentRank' ? b.containmentRank
        : field === 'cartManagerRank' ? b.cartManagerRank
        : b.rank;
      return (aVal ?? Infinity) - (bVal ?? Infinity);
    });
    sorted.forEach((s, i) => rankMap.set(s.id, i + 1));
    return rankMap;
  }, [quickFilteredStores, getActiveSortBy]);

  useEffect(() => {
    const key = filteredStores.map(s => `${s.poiId || s.id}:${s.name}:${s.store_notes?.length ?? 0}`).join(',');
    if (key !== prevFilteredKeyRef.current) {
      prevFilteredKeyRef.current = key;
      const storesWithRank = filteredStores.map(s => ({
        ...s,
        displayRank: storeSequentialRank.get(s.id) ?? null
      }));
      onProcessedStoresChange?.(storesWithRank);
    }
  });

  const sortedStores = useMemo(() => [...filteredStores].sort((a, b) => {
    // Column sort takes priority over rank sort
    if (columnSortBy) {
      let aValue: any;
      let bValue: any;
      const dir = columnSortOrder === 'asc' ? 1 : -1;

      if (columnSortBy === 'rank') {
        aValue = storeSequentialRank.get(a.id) ?? Infinity;
        bValue = storeSequentialRank.get(b.id) ?? Infinity;
      } else if (columnSortBy === 'status') {
        aValue = a.status === 'Customer' ? 0 : 1;
        bValue = b.status === 'Customer' ? 0 : 1;
      } else if (columnSortBy === 'crimeIndex') {
        const crimeOrder: Record<string, number> = { 'Critical': 4, 'High': 3, 'Medium': 2, 'Low': 1, 'No Data': 0 };
        aValue = crimeOrder[getCrimeIndex(a.crimeIncidents)] ?? 0;
        bValue = crimeOrder[getCrimeIndex(b.crimeIncidents)] ?? 0;
      } else if (columnSortBy === 'highwayProximity') {
        aValue = a.highwayProximity ? parseFloat(a.highwayProximity) : Infinity;
        bValue = b.highwayProximity ? parseFloat(b.highwayProximity) : Infinity;
      } else if (columnSortBy === 'cartOrdinance') {
        aValue = (a.hasCartPenalty || a.requiresCompliance) ? 1 : 0;
        bValue = (b.hasCartPenalty || b.requiresCompliance) ? 1 : 0;
      } else if (columnSortBy === 'weather') {
        aValue = (a.hotDays ?? 0) + (a.coldDays ?? 0);
        bValue = (b.hotDays ?? 0) + (b.coldDays ?? 0);
      } else if (columnSortBy === 'customerSince') {
        const aDate = a.firstPurchaseDate;
        const bDate = b.firstPurchaseDate;
        if (!aDate && !bDate) return 0;
        if (!aDate) return 1;
        if (!bDate) return -1;
        return (new Date(aDate).getTime() - new Date(bDate).getTime()) * dir;
      } else if (columnSortBy === 'lastPurchase') {
        const aDate = a.lastPurchaseDate;
        const bDate = b.lastPurchaseDate;
        if (!aDate && !bDate) return 0;
        if (!aDate) return 1;
        if (!bDate) return -1;
        return (new Date(aDate).getTime() - new Date(bDate).getTime()) * dir;
      }

      if (aValue < bValue) return -1 * dir;
      if (aValue > bValue) return 1 * dir;
      return 0;
    }

    // Default: rank-based sorting
    if (!getActiveSortBy) return 0;

    let aValue: any;
    let bValue: any;

    if (getActiveSortBy === 'rank') {
      aValue = a.rank ?? Infinity;
      bValue = b.rank ?? Infinity;
    } else if (getActiveSortBy === 'purchekRank') {
      aValue = a.purchekRank ?? Infinity;
      bValue = b.purchekRank ?? Infinity;
    } else if (getActiveSortBy === 'containmentRank') {
      aValue = a.containmentRank ?? Infinity;
      bValue = b.containmentRank ?? Infinity;
    } else if (getActiveSortBy === 'cartManagerRank') {
      aValue = a.cartManagerRank ?? Infinity;
      bValue = b.cartManagerRank ?? Infinity;
    }

    if (aValue < bValue) return -1;
    if (aValue > bValue) return 1;
    return 0;
  }), [filteredStores, columnSortBy, columnSortOrder, storeSequentialRank, getActiveSortBy]);

  // Pagination logic with sorted stores
  const totalPages = Math.ceil(sortedStores.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedStores = sortedStores.slice(startIndex, endIndex);

  // Reset to page 1 when filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [activeFilters]);

  // Handle filter application from the filter panel
  const handleApplyFilters = (filters: SitesFilterParams) => {
    setAppliedFilters(filters);
    setIsSitesFilterOpen(false);
    setCurrentPage(1);
  };

  // Convert stores to MapStore[] for GoogleMapView
  const mapStores: MapStore[] = useMemo(() => {
    const base = showTop20 ? sortedStores.slice(0, 20) : sortedStores;
    return base
      .filter(s => s.lat && s.lng)
      .map(s => {
        const solutions = typeof s.solutions === 'string' ? s.solutions.split(',').map(x => x.trim()) : (s.solutions || []);
        const isCustomer = s.status === 'Customer';
        return {
          id: s.poiId || s.storeId || String(s.id),
          brand_name: s.brandName || brandName || undefined,
          site: s.name,
          latitude: s.lat!,
          longitude: s.lng!,
          customer_id: isCustomer ? 'yes' : undefined,
          channel: s.channel ?? undefined,
          address: s.location ?? undefined,
          city: s.city ?? undefined,
          state: s.state ?? undefined,
          zipcode: s.zipCode ?? undefined,
          purchek_install_date: solutions.includes('Purchek') ? 'installed' : undefined,
          containment_install_date: solutions.includes('Cart Containment') ? 'installed' : undefined,
          cart_manager_install_date: solutions.includes('Cart Manager') ? 'installed' : undefined,
          subscription_start_date: solutions.includes('Subscription') ? 'installed' : undefined,
          paired_wheel_install_date: solutions.includes('Paired Wheel') ? 'installed' : undefined,
          estimated_no_of_carts_per_store: s.estimatedCarts,
          geo: {
            city: s.city ?? undefined,
            state_code: s.state ?? undefined,
            total_crime_incidents: s.crimeIncidents ?? 0,
          },
          nearby_penetration: {
            plaza_cm_customer_count: s.plaza_cm_customer_count ?? 0,
          },
          recommendations: s.recommendations,
          recommendation_rank: storeSequentialRank.get(s.id) ?? null,
          total_crime_amount_usd: s.totalCrimeAmountUsd ?? null,
          video_incident_count: s.videoIncidentCount ?? null,
          is_customer: isCustomer,
          updated_store_ids: s.storeId ? [{ store_id: s.storeId }] : null,
        };
      });
  }, [showTop20, sortedStores, storeSequentialRank, brandName]);

  const handleToggleStore = useCallback((poiId: string) => {
    toggleStoreInExportStore(brandKey, poiId);
    // Defer parent notification to avoid synchronous re-render cascade
    const current = useExportStore.getState().selectedStoresByBrand[brandKey] || new Set<string>();
    queueMicrotask(() => onSelectedStoresChange?.(current));
  }, [toggleStoreInExportStore, brandKey, onSelectedStoresChange]);

  // Export selection config for GoogleMapView
  const mapExportSelection = useMemo(() => {
    if (!handleToggleStore) return undefined;
    return {
      selectedIds: selectedStoreIds,
      onToggle: handleToggleStore,
    };
  }, [selectedStoreIds, handleToggleStore]);

  const handleStreetViewClick = useCallback((position: { lat: number; lng: number }, name?: string) => {
    setStreetViewStore({ lat: position.lat, lng: position.lng, name });
  }, []);

  const handleToggleAll = () => {
    if (selectedStoreIds.size === filteredStores.length && filteredStores.length > 0) {
      setStoresInExportStore(brandKey, new Set());
      onSelectedStoresChange?.(new Set());
    } else {
      const allIds = new Set(filteredStores.map(s => s.poiId || '').filter(Boolean));
      setStoresInExportStore(brandKey, allIds);
      onSelectedStoresChange?.(allIds);
    }
  };


  const isAllSelected = filteredStores.length > 0 && selectedStoreIds.size === filteredStores.length;
  const isSomeSelected = selectedStoreIds.size > 0 && selectedStoreIds.size < filteredStores.length;

  // Calculate the number of active filters
  const activeFilterCount = [
    appliedFilters.status && appliedFilters.status !== 'All Status' ? 1 : 0,
    appliedFilters.solutions && appliedFilters.solutions !== 'All Solutions' ? 1 : 0,
    appliedFilters.crimeLevel && appliedFilters.crimeLevel.length > 0 ? 1 : 0,
    appliedFilters.cartPenalty && appliedFilters.cartPenalty !== 'All' ? 1 : 0,
    appliedFilters.quickFilters && appliedFilters.quickFilters.length > 0 ? 1 : 0,
    appliedFilters.whitespace && appliedFilters.whitespace.length > 0 ? 1 : 0,
    appliedFilters.states && appliedFilters.states.length > 0 ? 1 : 0,
    appliedFilters.counties && appliedFilters.counties.length > 0 ? 1 : 0,
    appliedFilters.cities && appliedFilters.cities.length > 0 ? 1 : 0,
    appliedFilters.purchekNeighborhoodSites && (appliedFilters.purchekNeighborhoodSites.min > 0 || appliedFilters.purchekNeighborhoodSites.max < 100) ? 1 : 0,
    appliedFilters.purchekPenetration && (appliedFilters.purchekPenetration.min > 0 || appliedFilters.purchekPenetration.max < 100) ? 1 : 0,
    appliedFilters.hotDays && (appliedFilters.hotDays.min > 0 || appliedFilters.hotDays.max < 365) ? 1 : 0,
    appliedFilters.coldDays && (appliedFilters.coldDays.min > 0 || appliedFilters.coldDays.max < 365) ? 1 : 0,
    appliedFilters.locatedInShoppingComplex ? 1 : 0,
    appliedFilters.pairedWheelPenetration && (appliedFilters.pairedWheelPenetration.min > 0 || appliedFilters.pairedWheelPenetration.max < 100) ? 1 : 0,
    appliedFilters.singleWheelReplacement && (appliedFilters.singleWheelReplacement.min > 0 || appliedFilters.singleWheelReplacement.max < 100) ? 1 : 0,
    appliedFilters.containmentNeighborhoodSites && (appliedFilters.containmentNeighborhoodSites.min > 0 || appliedFilters.containmentNeighborhoodSites.max < 100) ? 1 : 0,
    appliedFilters.containmentPenetration && (appliedFilters.containmentPenetration.min > 0 || appliedFilters.containmentPenetration.max < 100) ? 1 : 0,
    appliedFilters.subBrands && appliedFilters.subBrands.length > 0 ? 1 : 0,
    appliedFilters.neighbourhoodPushoutIncidents && appliedFilters.neighbourhoodPushoutIncidents.length > 0 ? 1 : 0,
    appliedFilters.storePushoutIncidents && appliedFilters.storePushoutIncidents.length > 0 ? 1 : 0,
    appliedFilters.neighbourhoodRecoveryAmount && appliedFilters.neighbourhoodRecoveryAmount.length > 0 ? 1 : 0,
    appliedFilters.storeRecoveryAmount && appliedFilters.storeRecoveryAmount.length > 0 ? 1 : 0,
    appliedFilters.storeDensity && appliedFilters.storeDensity.length > 0 ? 1 : 0,
    appliedFilters.highwayProximity && appliedFilters.highwayProximity.length > 0 ? 1 : 0,
  ].reduce((sum, count) => sum + count, 0);

  return (
    <div className="all-sites">
      {/* Stores Table */}
      <div className="stores-table-section">

        <div className="table-controls">
          <div className="stores-search-box">
            <input
              type="text"
              placeholder="Search by Site Name/ Address/ City / Zipcode"
              className="stores-search-input"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <HiOutlineMagnifyingGlass className="stores-search-icon" />
            {searchQuery && (
              <button className="stores-search-clear" onClick={() => setSearchQuery('')} type="button">
                <HiX size={14} />
              </button>
            )}
          </div>
          <div className="stores-count-display">
            <strong>{filteredStores.length}</strong> store{filteredStores.length !== 1 ? 's' : ''}
          </div>
          <div className="table-controls-right">
            <button className="filters-btn" onClick={() => setIsSitesFilterOpen(true)}>
              <HiOutlineFilter />
              Filters
              {activeFilterCount > 0 && (
                <span className="filter-badge">{activeFilterCount}</span>
              )}
            </button>
            <div className="view-toggle">
              <button
                className={`view-toggle-btn ${viewMode === 'list' ? 'active' : ''}`}
                onClick={() => setViewMode('list')}
                title="List View"
              >
                <HiOutlineListBullet />
              </button>
              <button
                className={`view-toggle-btn ${viewMode === 'map' ? 'active' : ''}`}
                onClick={() => setViewMode('map')}
                title="Map View"
              >
                <HiOutlineMapPin />
              </button>
            </div>
            {/* <button className="add-store-id-btn" onClick={onImport}>
              <HiOutlineArrowUpTray /> Import
            </button> */}
            <button className="add-store-id-btn" onClick={() => setShowCSVModal(true)}>
              Add Store No
            </button>
            {idsApplied && !publishCompleted && (
              <button
                className="publish-btn"
                onClick={handlePublishToSheet}
                disabled={publishing}
              >
                {publishing ? 'Publishing...' : 'Publish Site No'}
              </button>
            )}
          </div>
        </div>

        <div className="shortcuts-filters">
          <Tooltip content="Show all stores with any sales opportunity" side="top">
            <button
              className={`filter-btn blue ${activeFilters.has('any-opportunity') ? 'active' : ''}`}
              onClick={() => {
                if (activeFilters.has('any-opportunity')) {
                  setActiveFilters(new Set<FilterType>());
                } else {
                  setActiveFilters(new Set<FilterType>(['any-opportunity']));
                }
              }}
            >
              <img src={require('../../assets/solution-icons/AnyOpportuinity.png')} alt="Any Opportunity" className="filter-icon" />
              Sites with Any Opportunity
              <span className="filter-count blue">{anyOpportunityCount}</span>
            </button>
          </Tooltip>
          <button
              className={`filter-btn blue ${activeFilters.has('purchek') ? 'active' : ''}`}
              onClick={() => {
                const newFilters = new Set(activeFilters);
                newFilters.delete('any-opportunity');
                if (newFilters.has('purchek')) {
                  newFilters.delete('purchek');
                } else {
                  newFilters.add('purchek');
                }
                setActiveFilters(newFilters);
              }}
            >
              <img src={require('../../assets/solution-icons/Purchek.png')} alt="Purchek" className="filter-icon" />
              Purchek
              <span className="filter-count blue">{purchekCount}</span>
            </button>
            <button
              className={`filter-btn cyan ${activeFilters.has('containment') ? 'active' : ''}`}
              onClick={() => {
                const newFilters = new Set(activeFilters);
                newFilters.delete('any-opportunity');
                if (newFilters.has('containment')) {
                  newFilters.delete('containment');
                } else {
                  newFilters.add('containment');
                }
                setActiveFilters(newFilters);
              }}
            >
              <img src={require('../../assets/solution-icons/CartControl.png')} alt="Cart Containment" className="filter-icon" />
              Cart Containment
              <span className="filter-count cyan">{containmentCount}</span>
            </button>
          <button
              className={`filter-btn green ${activeFilters.has('cart-manager') ? 'active' : ''}`}
              onClick={() => {
                const newFilters = new Set(activeFilters);
                newFilters.delete('any-opportunity');
                if (newFilters.has('cart-manager')) {
                  newFilters.delete('cart-manager');
                } else {
                  newFilters.add('cart-manager');
                }
                setActiveFilters(newFilters);
              }}
            >
              <img src={require('../../assets/solution-icons/CartManager.png')} alt="Cart Manager" className="filter-icon" />
              Cart Manager
              <span className="filter-count green">{cartManagerCount}</span>
            </button>
          <button
              className={`filter-btn purple ${activeFilters.has('subscription') ? 'active' : ''}`}
              onClick={() => {
                const newFilters = new Set(activeFilters);
                newFilters.delete('any-opportunity');
                if (newFilters.has('subscription')) {
                  newFilters.delete('subscription');
                } else {
                  newFilters.add('subscription');
                }
                setActiveFilters(newFilters);
              }}
            >
              <img src={require('../../assets/solution-icons/Subscription.png')} alt="Subscription" className="filter-icon" />
              Subscription
              <span className="filter-count purple">{subscriptionCount}</span>
            </button>
          <button
              className={`filter-btn orange ${activeFilters.has('paired-wheel') ? 'active' : ''}`}
              onClick={() => {
                const newFilters = new Set(activeFilters);
                newFilters.delete('any-opportunity');
                if (newFilters.has('paired-wheel')) {
                  newFilters.delete('paired-wheel');
                } else {
                  newFilters.add('paired-wheel');
                }
                setActiveFilters(newFilters);
              }}
            >
              <img src={require('../../assets/solution-icons/PairedWheel.png')} alt="Paired Wheel" className="filter-icon" />
              Paired Wheel
              <span className="filter-count orange">{pairedWheelCount}</span>
            </button>
          {!showMoreShortcuts && (
            <button
                className="filter-btn more-btn"
                onClick={() => setShowMoreShortcuts(true)}
              >
                More
                <HiChevronRight className="more-icon" />
              </button>
          )}
          {showMoreShortcuts && (
            <>
              <button
                  className={`filter-btn red ${activeFilters.has('high-crime') ? 'active' : ''}`}
                  onClick={() => {
                    const newFilters = new Set(activeFilters);
                    newFilters.delete('any-opportunity');
                    if (newFilters.has('high-crime')) {
                      newFilters.delete('high-crime');
                    } else {
                      newFilters.add('high-crime');
                    }
                    setActiveFilters(newFilters);
                  }}
                >
                  High Crime Sites
                  <span className="filter-count red">{highCrimeCount}</span>
                </button>
              <button
                  className={`filter-btn red ${activeFilters.has('critical-crime') ? 'active' : ''}`}
                  onClick={() => {
                    const newFilters = new Set(activeFilters);
                    newFilters.delete('any-opportunity');
                    if (newFilters.has('critical-crime')) {
                      newFilters.delete('critical-crime');
                    } else {
                      newFilters.add('critical-crime');
                    }
                    setActiveFilters(newFilters);
                  }}
                >
                  Critical Crime Sites
                  <span className="filter-count red">{criticalCrimeCount}</span>
                </button>
              <button
                  className={`filter-btn gray ${activeFilters.has('cart-ordinance') ? 'active' : ''}`}
                  onClick={() => {
                    const newFilters = new Set(activeFilters);
                    newFilters.delete('any-opportunity');
                    if (newFilters.has('cart-ordinance')) {
                      newFilters.delete('cart-ordinance');
                    } else {
                      newFilters.add('cart-ordinance');
                    }
                    setActiveFilters(newFilters);
                  }}
                >
                  Cart Ordinance
                  <span className="filter-count gray">{cartOrdinanceCount}</span>
                </button>
              <button
                  className={`filter-btn pink ${activeFilters.has('extreme-weather') ? 'active' : ''}`}
                  onClick={() => {
                    const newFilters = new Set(activeFilters);
                    newFilters.delete('any-opportunity');
                    if (newFilters.has('extreme-weather')) {
                      newFilters.delete('extreme-weather');
                    } else {
                      newFilters.add('extreme-weather');
                    }
                    setActiveFilters(newFilters);
                  }}
                >
                  Extreme Weather
                  <span className="filter-count pink">{extremeWeatherCount}</span>
                </button>
              <button
                  className="filter-btn more-btn"
                  onClick={() => setShowMoreShortcuts(false)}
                >
                  <HiChevronLeft className="more-icon" />
                  Less
                </button>
            </>
          )}
        </div>

        {viewMode === 'map' ? (
          <div className="stores-map-container">
            {filteredStores.length === 0 && !loading ? (
              <div className="no-stores-message">
                <HiOutlineOfficeBuilding size={40} />
                <p>{stores.length === 0 ? 'No stores available' : 'No opportunities available for the applied filters'}</p>
              </div>
            ) : (
              <>
                <div className="map-top-controls">
                  <button
                    className={`top20-btn${showTop20 ? ' active' : ''}`}
                    onClick={() => setShowTop20(prev => !prev)}
                  >
                    <input
                      type="checkbox"
                      checked={showTop20}
                      onChange={() => {}}
                      className="top20-checkbox"
                    />
                    Show Top 20 Opportunities
                  </button>
                </div>
                <div className="allsites-map-inline" style={{ position: 'relative' }}>
                  <GoogleMapView
                    stores={mapStores}
                    fitBounds={true}
                    showHeatmap={showHeatmap}
                    showDetailedMarkers={true}
                    exportSelection={mapExportSelection}
                    onStreetViewClick={handleStreetViewClick}
                    height="100%"
                  />
                  <HeatmapToggle showHeatmap={showHeatmap} onToggle={setShowHeatmap} />
                  <div className="map-legend">
                    <span className="map-legend-item"><span className="map-legend-dot blue"></span>Customer</span>
                    <span className="map-legend-item"><span className="map-legend-dot red"></span>Non-Customer</span>
                    <span className="map-legend-divider"></span>
                    <span className="map-legend-item"><span className="map-legend-badge">P</span>Purchek</span>
                    <span className="map-legend-item"><span className="map-legend-badge">CC</span>Cart Containment</span>
                    <span className="map-legend-item"><span className="map-legend-badge">CM</span>Cart Manager</span>
                    <span className="map-legend-item"><span className="map-legend-badge">S</span>Subscription</span>
                  </div>
                  {mapStores.length === 0 && (
                    <div className="allsites-map-empty">
                      <p>No stores match this filter.</p>
                    </div>
                  )}
                </div>
                {streetViewStore && (
                  <StreetViewPanel
                    lat={streetViewStore.lat}
                    lng={streetViewStore.lng}
                    storeName={streetViewStore.name}
                    onClose={() => setStreetViewStore(null)}
                  />
                )}
              </>
            )}
          </div>
        ) : (
        <>
        {selectedStoreIds.size > 0 && (
          <div className="selection-action-strip">
            <div className="selection-action-strip-left">
              <span className="selection-action-strip-label">{selectedStoreIds.size} selected</span>
              <button className="selection-action-strip-btn save" onClick={onSaveStoresToList}>
                <HiOutlineBookmark size={14} />
                Add to list
              </button>
              <button className="selection-action-strip-btn download" onClick={onDownloadXlsx}>
                <svg className="selection-action-strip-icon" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
                Download as .xlsx
              </button>
              <button className="selection-action-strip-btn export-crm" onClick={onExportCrm}>
                <HiOutlineArrowUpTray size={14} />
                Export to CRM
              </button>
            </div>
            <button
              className="selection-action-strip-btn clear"
              onClick={() => { setStoresInExportStore(brandKey, new Set()); onSelectedStoresChange?.(new Set()); }}
            >
              Clear
            </button>
          </div>
        )}
        <div className="stores-table-container">
          {loading ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
              <Spinner size="small" />
            </div>
          ) : filteredStores.length === 0 ? (
            <div className="no-stores-message">
              <HiOutlineOfficeBuilding size={40} />
              <p>{stores.length === 0 ? 'No stores available' : 'No opportunities available for the applied filters'}</p>
            </div>
          ) : (
            <table className="stores-table">
            <thead>
              <tr>
                <th>
                  <input
                    type="checkbox"
                    className="store-checkbox"
                    checked={isAllSelected}
                    ref={(el) => { if (el) el.indeterminate = isSomeSelected; }}
                    onChange={handleToggleAll}
                  />
                </th>
                <th style={{ width: '40px' }} className="sortable-header" onClick={() => handleColumnSort('rank')}>
                  <div className="header-sort">
                    Rank
                    {columnSortBy === 'rank' ? (columnSortOrder === 'asc' ? <HiChevronUp size={14} /> : <HiChevronDown size={14} />) : <HiChevronUpDown size={14} className="sort-icon-muted" />}
                  </div>
                </th>
                <th style={{ width: '40px' }}></th>
                <th>Store</th>
                <th className="sortable-header" onClick={() => handleColumnSort('status')}>
                  <div className="header-sort">
                    Status
                    {columnSortBy === 'status' ? (columnSortOrder === 'asc' ? <HiChevronUp size={14} /> : <HiChevronDown size={14} />) : <HiChevronUpDown size={14} className="sort-icon-muted" />}
                  </div>
                </th>
                <th>
                  <Tooltip content={getTooltipText('installedSolutions')} side="top">
                    <div>Installed Solutions</div>
                  </Tooltip>
                </th>
                <th className="sortable-header" onClick={() => handleColumnSort('customerSince')}>
                  <div className="header-sort">
                    Customer Since
                    {columnSortBy === 'customerSince' ? (columnSortOrder === 'asc' ? <HiChevronUp size={14} /> : <HiChevronDown size={14} />) : <HiChevronUpDown size={14} className="sort-icon-muted" />}
                  </div>
                </th>
                <th style={{ minWidth: '200px' }}>
                  <Tooltip content={getTooltipText('whitespace')} side="top">
                    <div>Whitespace</div>
                  </Tooltip>
                </th>
                <th style={{ minWidth: '220px' }}>
                  <Tooltip content={getTooltipText('recommendedSolutions')} side="top">
                    <div>Recommended Solutions</div>
                  </Tooltip>
                </th>
                <th className="sortable-header" onClick={() => handleColumnSort('crimeIndex')}>
                  <Tooltip content={getTooltipText('crimeData')} side="top">
                    <div className="header-sort">
                      Crime Index
                      {columnSortBy === 'crimeIndex' ? (columnSortOrder === 'asc' ? <HiChevronUp size={14} /> : <HiChevronDown size={14} />) : <HiChevronUpDown size={14} className="sort-icon-muted" />}
                    </div>
                  </Tooltip>
                </th>
                <th>
                  <Tooltip content="Gatekeeper Account Penetration – the percentage of customer stores / total stores within 5 mile radius of the reference store" side="top">
                    <div>GK Account Penetration</div>
                  </Tooltip>
                </th>
                <th className="sortable-header" onClick={() => handleColumnSort('highwayProximity')}>
                  <Tooltip content="Distance to the nearest highway (in miles)" side="top">
                    <div className="header-sort">
                      Highway Proximity
                      {columnSortBy === 'highwayProximity' ? (columnSortOrder === 'asc' ? <HiChevronUp size={14} /> : <HiChevronDown size={14} />) : <HiChevronUpDown size={14} className="sort-icon-muted" />}
                    </div>
                  </Tooltip>
                </th>
                <th className="sortable-header" onClick={() => handleColumnSort('cartOrdinance')}>
                  <Tooltip content={getTooltipText('cartPenalty')} side="top">
                    <div className="header-sort">
                      Cart Ordinance
                      {columnSortBy === 'cartOrdinance' ? (columnSortOrder === 'asc' ? <HiChevronUp size={14} /> : <HiChevronDown size={14} />) : <HiChevronUpDown size={14} className="sort-icon-muted" />}
                    </div>
                  </Tooltip>
                </th>
                <th className="sortable-header" onClick={() => handleColumnSort('weather')}>
                  <div className="header-sort">
                    Weather
                    {columnSortBy === 'weather' ? (columnSortOrder === 'asc' ? <HiChevronUp size={14} /> : <HiChevronDown size={14} />) : <HiChevronUpDown size={14} className="sort-icon-muted" />}
                  </div>
                </th>
                <th>Total Recovery</th>
                <th>Pushout Prevented</th>
              </tr>
            </thead>
            <tbody>
              {paginatedStores.map((store, index) => (
                <React.Fragment key={store.id}>
                  <tr className={expandedRow === store.id ? 'expanded' : ''} onClick={(e) => {
                      if ((e.target as HTMLElement).closest('.map-btn') || (e.target as HTMLElement).closest('.store-checkbox') || (e.target as HTMLElement).closest('.add-to-list-btn-sites') || (e.target as HTMLElement).closest('.add-notes-btn-sites')) {
                        return;
                      }
                      toggleRow(store.id);
                    }} style={{ cursor: 'pointer' }}>
                    <td>
                      <input
                        type="checkbox"
                        className="store-checkbox"
                        checked={selectedStoreIds.has(store.poiId || '')}
                        onChange={() => handleToggleStore(store.poiId || '')}
                        onClick={(e) => e.stopPropagation()}
                      />
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      <span className="rank-badge">
                        {storeSequentialRank.get(store.id) ?? '-'}
                      </span>
                    </td>
                    <td>
                      <button
                        className="expand-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleRow(store.id);
                        }}
                      >
                        <HiChevronRight className={`expand-icon ${expandedRow === store.id ? 'expanded' : ''}`} />
                      </button>
                    </td>
                    <td>
                      <div className="location-cell">
                        <div className="store-name">
                          <span className="store-name-text" title={store.storeId ? `${store.brandName || store.name} ${store.storeId}` : store.name || store.brandName}>{store.storeId ? `${store.brandName || store.name} ${store.storeId}` : store.name || store.brandName}</span>
                          {store.store_notes && store.store_notes.length > 0 && (
                            <HiOutlinePencilAlt className="store-indicator-icon" title={`${store.store_notes.length} note${store.store_notes.length > 1 ? 's' : ''}`} />
                          )}
                          {savedStores.has(store.poiId || '') && (
                            <HiBookmark className="store-indicator-icon store-indicator-saved" title="Saved to list" />
                          )}
                          {(store.plaza_cm_customer_count ?? 0) > 0 && (
                            <HiOutlineOfficeBuilding className="plaza-icon" title="Part of Shopping Complex" />
                          )}
                        </div>
                        <div className="poi-id">{store.poiId}</div>
                        <div>{store.location}</div>
                        <div className="zip-code">
                          {store.city !== '-' || store.state !== '-' ? (
                            `${store.city !== '-' ? store.city + ', ' : ''}${store.state}${store.zipCode ? ' ' + String(store.zipCode) : ''}`
                          ) : (
                            '-'
                          )}
                        </div>
                        <Tooltip
                          content={!store.totalDensity ? 'No stores around 0.5 miles radius' : `${store.totalDensity} stores in 0.5 miles radius`}
                          side="top"
                        >
                          <span className={`store-density-tag density-${getStoreDensityLabel(store.totalDensity).toLowerCase()}`} style={{ cursor: 'default' }}>
                            Store Density : {getStoreDensityLabel(store.totalDensity)}
                          </span>
                        </Tooltip>
                        <div className="store-hover-actions">
                          <button
                            className="map-btn"
                            onClick={(e) => { e.stopPropagation(); handleViewDetails(store); }}
                            title="View Store Details"
                          >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                              <circle cx="12" cy="12" r="3" />
                            </svg>
                            <span>Details</span>
                          </button>
                          <button
                            className="add-notes-btn-sites"
                            title="Add Notes"
                            onClick={(e) => { e.stopPropagation(); handleNotesClick(store.storeId ? `${store.brandName || store.name} ${store.storeId}` : store.name || store.brandName || '', store.poiId as string); }}
                          >
                            <HiOutlinePencilAlt className="notes-icon-sites" />
                            <span>Add Note</span>
                            {store.store_notes && store.store_notes.length > 0 && (
                              <span className="notes-badge">{store.store_notes.length}</span>
                            )}
                          </button>
                          <button
                            className={`add-to-list-btn-sites ${savedStores.has(store.poiId || '') ? 'saved' : ''}`}
                            onClick={(e) => { e.stopPropagation(); handleAddToListClick(store); }}
                            title="Save to List"
                          >
                            {savedStores.has(store.poiId || '') ? (
                              <><HiBookmark size={14} /><span>Saved</span></>
                            ) : (
                              <><HiOutlineBookmark size={14} /><span>Save to List</span></>
                            )}
                          </button>
                        </div>
                      </div>
                    </td>
                    <td>
                      <span className={`status-badge ${store.status === 'Customer' ? 'customer' : 'non-customer'}`}>
                        {store.status ?? 'Non-Customer'}
                      </span>
                    </td>
                    <td className="solutions-cell">
                      <div className="solutions-container">
                        {store.solutions && store.solutions !== 'None' ? (
                          <>
                            {store.solutions.split(', ').slice(0, expandedSolutions.has(store.id) ? undefined : 2).map((solution, idx) => (
                              <span key={idx} className="solution-badge installed">
                                {solution}
                              </span>
                            ))}
                            {!expandedSolutions.has(store.id) && store.solutions.split(', ').length > 2 && (
                              <button
                                className="solution-badge installed expand-more"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  const newExpanded = new Set(expandedSolutions);
                                  newExpanded.add(store.id);
                                  setExpandedSolutions(newExpanded);
                                }}
                              >
                                +{store.solutions.split(', ').length - 2}
                              </button>
                            )}
                            {expandedSolutions.has(store.id) && store.solutions.split(', ').length > 2 && (
                              <button
                                className="solution-badge installed expand-less"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  const newExpanded = new Set(expandedSolutions);
                                  newExpanded.delete(store.id);
                                  setExpandedSolutions(newExpanded);
                                }}
                              >
                                - Less
                              </button>
                            )}
                          </>
                        ) : (
                          <span className="solution-text">{store.status === 'Non-Customer' ? 'No Solution Installed' : 'None'}</span>
                        )}
                      </div>
                    </td>
                    <td>
                      <span className="last-purchase-text">{store.firstPurchaseDate || '-'}</span>
                    </td>
                    <td className="whitespace-cell">
                      <div className="solutions-container">
                        {store.whitespace && store.whitespace.length > 0 ? (
                          <>
                            {store.whitespace.slice(0, expandedWhitespace.has(store.id) ? undefined : 2).map((ws, idx) => (
                              <span key={idx} className="solution-badge whitespace">
                                {ws}
                              </span>
                            ))}
                            {!expandedWhitespace.has(store.id) && store.whitespace.length > 2 && (
                              <button
                                className="solution-badge whitespace expand-more"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  const newExpanded = new Set(expandedWhitespace);
                                  newExpanded.add(store.id);
                                  setExpandedWhitespace(newExpanded);
                                }}
                              >
                                +{store.whitespace.length - 2}
                              </button>
                            )}
                            {expandedWhitespace.has(store.id) && store.whitespace.length > 2 && (
                              <button
                                className="solution-badge whitespace expand-less"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  const newExpanded = new Set(expandedWhitespace);
                                  newExpanded.delete(store.id);
                                  setExpandedWhitespace(newExpanded);
                                }}
                              >
                                - Less
                              </button>
                            )}
                          </>
                        ) : (
                          <Tooltip content="The store is already protected" side="top">
                            <span className="solution-text">None</span>
                          </Tooltip>
                        )}
                      </div>
                    </td>
                    <td className="opportunity-cell">
                      <div className="opportunity-container">
                        {store.recommendations && (
                          store.recommendations.purchek.length > 0 ||
                          store.recommendations.containment.length > 0 ||
                          store.recommendations.cartManager.length > 0 ||
                          store.recommendations.subscription.length > 0 ||
                          store.recommendations.pairedWheel.length > 0
                        ) ? (
                          <>
                            {(() => {
                              const recommendations = [];
                              if (store.recommendations.purchek.length > 0) recommendations.push({ type: 'purchek', label: 'Purchek' });
                              if (store.recommendations.containment.length > 0) recommendations.push({ type: 'containment', label: 'Containment' });
                              if (store.recommendations.cartManager.length > 0) recommendations.push({ type: 'cartManager', label: 'Cart Manager' });
                              if (store.recommendations.subscription.length > 0) recommendations.push({ type: 'subscription', label: 'Subscription' });
                              if (store.recommendations.pairedWheel.length > 0) recommendations.push({ type: 'pairedWheel', label: 'Paired Wheel' });

                              const displayCount = expandedSolutions.has(store.id) ? recommendations.length : Math.min(2, recommendations.length);

                              return (
                                <>
                                  {recommendations.slice(0, displayCount).map((rec, idx) => (
                                    <span key={idx} className={`recommendation-badge ${rec.type}`}>
                                      {rec.label}
                                    </span>
                                  ))}
                                  {!expandedSolutions.has(store.id) && recommendations.length > 2 && (
                                    <button
                                      className="solution-badge installed expand-more"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        const newExpanded = new Set(expandedSolutions);
                                        newExpanded.add(store.id);
                                        setExpandedSolutions(newExpanded);
                                      }}
                                    >
                                      +{recommendations.length - 2}
                                    </button>
                                  )}
                                  {expandedSolutions.has(store.id) && recommendations.length > 2 && (
                                    <button
                                      className="solution-badge installed expand-less"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        const newExpanded = new Set(expandedSolutions);
                                        newExpanded.delete(store.id);
                                        setExpandedSolutions(newExpanded);
                                      }}
                                    >
                                      - Less
                                    </button>
                                  )}
                                </>
                              );
                            })()}
                          </>
                        ) : (
                          <span className="solution-text" title="No recommended solution based on the given Recommendation Configuration. The store may still be at risk and need protection">None</span>
                        )}
                      </div>
                    </td>
                      <td>
                        <Tooltip
                          content={store.crimeIncidents ? `${store.crimeIncidents.toLocaleString()} Annual Relevant Crimes Reported in ${store.city}` : 'Data not available'}
                          side="top"
                        >
                          <span style={{ cursor: 'default' }}>
                            {getCrimeIndex(store.crimeIncidents)}
                          </span>
                        </Tooltip>
                      </td>
                    <td>
                      <Tooltip
                        content={store.gkap ? `Nearby penetration within 5 miles radius: ${store.gkap}` : 'No nearby penetration data available'}
                        side="top"
                      >
                        <span style={{ cursor: 'default' }}>{store.gkap || '-'}</span>
                      </Tooltip>
                    </td>
                    <td>
                      <Tooltip
                        content={store.highwayProximity ? `${store.highwayProximity} miles to nearest highway` : 'Data not available'}
                        side="top"
                      >
                        <span className={`highway-proximity-badge ${getHighwayProximityLabel(store.highwayProximity).toLowerCase().replace(' ', '-')}`} style={{ cursor: 'default' }}>
                          {store.highwayProximity ? `${parseFloat(store.highwayProximity).toFixed(1)} mi` : 'No Data'}
                        </span>
                      </Tooltip>
                    </td>
                    <td>
                      <Tooltip
                        content={
                          (store.hasCartPenalty || store.requiresCompliance)
                            ? (store.lawSummary || 'Cart abandonment ordinance is active in this state.')
                            : 'No cart abandonment ordinance in this state.'
                        }
                        side="top"
                      >
                        <span className={`penalty-badge ${(store.hasCartPenalty || store.requiresCompliance) ? 'yes' : 'no'}`} style={{ cursor: 'default' }}>
                          {(store.hasCartPenalty || store.requiresCompliance) ? 'Yes' : 'No'}
                        </span>
                      </Tooltip>
                    </td>
                    <td>
                      <Tooltip
                        content={
                          (store.hotDays || store.coldDays)
                            ? `Hot Days (Extreme Heat 90°F or higher): ${store.hotDays ?? 0} | Cold Days (Extreme Cold 32°F or lower): ${store.coldDays ?? 0}`
                            : 'Data not available'
                        }
                        side="top"
                      >
                        <div className="weather-cell" style={{ cursor: 'default' }}>
                          {(store.hotDays != null && store.hotDays > 0) && (
                            <span className="weather-item hot">
                              <HiOutlineSun size={14} />
                              {store.hotDays}
                            </span>
                          )}
                          {(store.coldDays != null && store.coldDays > 0) && (
                            <span className="weather-item cold">
                              <BsSnow size={12} />
                              {store.coldDays}
                            </span>
                          )}
                          {(!store.hotDays && !store.coldDays) && <span className="weather-item none">-</span>}
                        </div>
                      </Tooltip>
                    </td>
                    <td className="recovery-cell">
                      {(store.shopliftingAmountUsd != null || store.totalCrimeAmountUsd != null)
                        ? <Tooltip content={`Neighbourhood Recovery: ${store.shopliftingAmountUsd != null ? `$${Math.round(store.shopliftingAmountUsd).toLocaleString()}` : 'N/A'} | Store Recovery: ${store.totalCrimeAmountUsd != null ? `$${Math.round(store.totalCrimeAmountUsd).toLocaleString()}` : 'N/A'}`} side="top">
                            <div className="recovery-rows">
                              {store.shopliftingAmountUsd != null && (
                                <div className="recovery-row">
                                  <span className="recovery-label">Neighbourhood</span>
                                  <span className="recovery-value">${Math.round(store.shopliftingAmountUsd).toLocaleString()}</span>
                                </div>
                              )}
                              {store.totalCrimeAmountUsd != null && (
                                <div className="recovery-row">
                                  <span className="recovery-label">Site</span>
                                  <span className="recovery-value">${Math.round(store.totalCrimeAmountUsd).toLocaleString()}</span>
                                </div>
                              )}
                            </div>
                          </Tooltip>
                        : <span className="solution-text">No Data</span>}
                    </td>
                    <td className="recovery-cell">
                      {(store.shopliftingIncidentCount != null || store.videoIncidentCount != null)
                        ? <Tooltip content={`Neighbourhood Pushouts Prevented: ${store.shopliftingIncidentCount != null ? store.shopliftingIncidentCount.toLocaleString() : 'N/A'} | Store Pushouts Prevented: ${store.videoIncidentCount != null ? store.videoIncidentCount.toLocaleString() : 'N/A'}`} side="top">
                            <div className="recovery-rows">
                              {store.shopliftingIncidentCount != null && (
                                <div className="recovery-row">
                                  <span className="recovery-label">Neighbourhood</span>
                                  <span className="recovery-value">{store.shopliftingIncidentCount.toLocaleString()}</span>
                                </div>
                              )}
                              {store.videoIncidentCount != null && (
                                <div className="recovery-row">
                                  <span className="recovery-label">Site</span>
                                  <span className="recovery-value">{store.videoIncidentCount.toLocaleString()}</span>
                                </div>
                              )}
                            </div>
                          </Tooltip>
                        : <span className="solution-text">No Data</span>}
                    </td>
                  </tr>
                  {expandedRow === store.id && (
                    <tr className="expanded-content">
                      <td colSpan={18}>
                        <div className="expanded-details">
                          
                          <div className="details-grid">
                          </div>
                          {(store.recommendations && (
                            store.recommendations.purchek.length > 0 ||
                            store.recommendations.containment.length > 0 ||
                            store.recommendations.cartManager.length > 0 ||
                            store.recommendations.subscription.length > 0 ||
                            store.recommendations.pairedWheel.length > 0
                          )) ? (
                            <div className="recommendations-section">
                              <div className="recommendations-list">
                                {store.recommendations.purchek.length > 0 && (
                                  <div className="recommendation-card">
                                    <div className="recommendation-title-bar">
                                      <span className="recommendation-title-text">Purchek Recommended</span>
                                      {store.recommendations.purchekBadge.matches && (
                                        <span className={`readiness-badge ${activeReadinessId === 'custom' || activeReadinessId?.toString().startsWith('custom_') ? 'custom' : activeReadinessId}`} title={store.recommendations.purchekBadge.tooltip}>{activeReadinessLevel?.name || 'High'} Readiness</span>
                                      )}
                                    </div>
                                    <ul className="recommendation-reasons-list">
                                      {store.recommendations.purchek.map((reason, idx) => {
                                        const reasonKey = `${store.id}-purchek-${idx}`;
                                        const isExpanded = expandedReasons.has(reasonKey);
                                        return (
                                          <li key={idx}>
                                            <span className="checkmark">✓</span>
                                            <div>
                                              <span className={`reason-text ${isExpanded ? 'expanded' : ''}`}>{reason}</span>
                                              {reason.length > 150 && (
                                                <button className="reason-toggle" onClick={(e) => { e.stopPropagation(); setExpandedReasons(prev => { const next = new Set(prev); next.has(reasonKey) ? next.delete(reasonKey) : next.add(reasonKey); return next; }); }}>
                                                  {isExpanded ? 'show less' : 'show more'}
                                                </button>
                                              )}
                                            </div>
                                          </li>
                                        );
                                      })}
                                    </ul>
                                  </div>
                                )}
                                {store.recommendations.containment.length > 0 && (
                                  <div className="recommendation-card">
                                    <div className="recommendation-title-bar">
                                      <span className="recommendation-title-text">Cart Containment Recommended</span>
                                      {store.recommendations.containmentBadge.matches && (
                                        <span className={`readiness-badge ${containmentActiveReadinessId === 'custom' || containmentActiveReadinessId?.toString().startsWith('custom_') ? 'custom' : containmentActiveReadinessId}`} title={store.recommendations.containmentBadge.tooltip}>{containmentActiveReadinessLevel?.name || 'High'} Readiness</span>
                                      )}
                                    </div>
                                    <ul className="recommendation-reasons-list">
                                      {store.recommendations.containment.map((reason, idx) => {
                                        const reasonKey = `${store.id}-containment-${idx}`;
                                        const isExpanded = expandedReasons.has(reasonKey);
                                        return (
                                          <li key={idx}>
                                            <span className="checkmark">✓</span>
                                            <div>
                                              <span className={`reason-text ${isExpanded ? 'expanded' : ''}`}>
                                                {reason} {" "}
                                                {reason === store.lawSummary && store.lawSource && (
                                                  <span className="law-source-link">
                                                    <a href={store.lawSource} target="_blank" rel="noopener noreferrer">Read More.</a>
                                                  </span>
                                                )}
                                              </span>
                                              {reason.length > 150 && (
                                                <button className="reason-toggle" onClick={(e) => { e.stopPropagation(); setExpandedReasons(prev => { const next = new Set(prev); next.has(reasonKey) ? next.delete(reasonKey) : next.add(reasonKey); return next; }); }}>
                                                  {isExpanded ? 'show less' : 'show more'}
                                                </button>
                                              )}
                                            </div>
                                          </li>
                                        );
                                      })}
                                    </ul>
                                  </div>
                                )}
                                {store.recommendations.cartManager.length > 0 && (
                                  <div className="recommendation-card">
                                    <div className="recommendation-title-bar">
                                      <span className="recommendation-title-text">Cart Manager Recommended</span>
                                      {store.recommendations.cartManagerBadge.matches && (
                                        <span className={`readiness-badge ${cartManagerActiveReadinessId === 'custom' || cartManagerActiveReadinessId?.toString().startsWith('custom_') ? 'custom' : cartManagerActiveReadinessId}`} title={store.recommendations.cartManagerBadge.tooltip}>{cartManagerActiveReadinessLevel?.name || 'High'} Readiness</span>
                                      )}
                                    </div>
                                    <ul className="recommendation-reasons-list">
                                      {store.recommendations.cartManager.map((reason, idx) => {
                                        const reasonKey = `${store.id}-cartManager-${idx}`;
                                        const isExpanded = expandedReasons.has(reasonKey);
                                        return (
                                          <li key={idx}>
                                            <span className="checkmark">✓</span>
                                            <div>
                                              <span className={`reason-text ${isExpanded ? 'expanded' : ''}`}>{reason}</span>
                                              {reason.length > 150 && (
                                                <button className="reason-toggle" onClick={(e) => { e.stopPropagation(); setExpandedReasons(prev => { const next = new Set(prev); next.has(reasonKey) ? next.delete(reasonKey) : next.add(reasonKey); return next; }); }}>
                                                  {isExpanded ? 'show less' : 'show more'}
                                                </button>
                                              )}
                                            </div>
                                          </li>
                                        );
                                      })}
                                    </ul>
                                  </div>
                                )}
                                {store.recommendations.subscription.length > 0 && (
                                  <div className="recommendation-card">
                                    <div className="recommendation-title-bar">
                                      <span className="recommendation-title-text">Subscription Recommended</span>
                                      {store.recommendations.subscriptionBadge.matches && (
                                        <span className={`readiness-badge ${subscriptionActiveReadinessId === 'custom' || subscriptionActiveReadinessId?.toString().startsWith('custom_') ? 'custom' : subscriptionActiveReadinessId}`} title={store.recommendations.subscriptionBadge.tooltip}>{subscriptionActiveReadinessLevel?.name || 'High'} Readiness</span>
                                      )}
                                    </div>
                                    <ul className="recommendation-reasons-list">
                                      {store.recommendations.subscription.map((reason, idx) => {
                                        const reasonKey = `${store.id}-subscription-${idx}`;
                                        const isExpanded = expandedReasons.has(reasonKey);
                                        return (
                                          <li key={idx}>
                                            <span className="checkmark">✓</span>
                                            <div>
                                              <span className={`reason-text ${isExpanded ? 'expanded' : ''}`}>{reason}</span>
                                              {reason.length > 150 && (
                                                <button className="reason-toggle" onClick={(e) => { e.stopPropagation(); setExpandedReasons(prev => { const next = new Set(prev); next.has(reasonKey) ? next.delete(reasonKey) : next.add(reasonKey); return next; }); }}>
                                                  {isExpanded ? 'show less' : 'show more'}
                                                </button>
                                              )}
                                            </div>
                                          </li>
                                        );
                                      })}
                                    </ul>
                                  </div>
                                )}
                                {store.recommendations.pairedWheel.length > 0 && (
                                  <div className="recommendation-card">
                                    <div className="recommendation-title-bar">
                                      <span className="recommendation-title-text">Paired Wheel Recommended</span>
                                      {store.recommendations.pairedWheelBadge.matches && (
                                        <span className={`readiness-badge ${pairedWheelActiveReadinessId === 'custom' || pairedWheelActiveReadinessId?.toString().startsWith('custom_') ? 'custom' : pairedWheelActiveReadinessId}`} title={store.recommendations.pairedWheelBadge.tooltip}>{pairedWheelActiveReadinessLevel?.name || 'High'} Readiness</span>
                                      )}
                                    </div>
                                    <ul className="recommendation-reasons-list">
                                      {store.recommendations.pairedWheel.map((reason, idx) => {
                                        const reasonKey = `${store.id}-pairedWheel-${idx}`;
                                        const isExpanded = expandedReasons.has(reasonKey);
                                        return (
                                          <li key={idx}>
                                            <span className="checkmark">✓</span>
                                            <div>
                                              <span className={`reason-text ${isExpanded ? 'expanded' : ''}`}>{reason}</span>
                                              {reason.length > 150 && (
                                                <button className="reason-toggle" onClick={(e) => { e.stopPropagation(); setExpandedReasons(prev => { const next = new Set(prev); next.has(reasonKey) ? next.delete(reasonKey) : next.add(reasonKey); return next; }); }}>
                                                  {isExpanded ? 'show less' : 'show more'}
                                                </button>
                                              )}
                                            </div>
                                          </li>
                                        );
                                      })}
                                    </ul>
                                  </div>
                                )}
                              </div>
                            </div>
                          ) : (
                            <div className="no-recommendations-section">
                              <p className="no-recommendations-message">
                                No recommended solution based on the given Recommendation Configuration. The store may still be at risk and need protection
                              </p>
                            </div>
                          )}

                         
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
            </table>
          )}
        </div>

        {filteredStores.length > 0 && !loading && (
          <div className="table-pagination">
            <div className="pagination-left">
              <label>Rows per page: </label>
              <CustomDropdown
                options={['10', '25', '50', '100']}
                value={String(itemsPerPage)}
                onChange={(value) => {
                  setItemsPerPage(Number(value));
                  setCurrentPage(1);
                }}
                className="page-size-dropdown"
              />
            </div>

            <span className="page-info">
              Page {currentPage} of {totalPages || 1}
            </span>

            <div className="pagination-buttons">
              <button
                onClick={() => setCurrentPage(1)}
                disabled={currentPage === 1}
                className="pagination-btn"
                title="First page"
              >
                <HiChevronDoubleLeft />
              </button>
              <button
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className="pagination-btn"
                title="Previous page"
              >
                <HiChevronLeft />
              </button>
              <button
                onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages || totalPages === 0}
                className="pagination-btn"
                title="Next page"
              >
                <HiChevronRight />
              </button>
              <button
                onClick={() => setCurrentPage(totalPages)}
                disabled={currentPage === totalPages || totalPages === 0}
                className="pagination-btn"
                title="Last page"
              >
                <HiChevronDoubleRight />
              </button>
            </div>
          </div>
        )}
        </>
        )}
      </div>

      <CSVUploadModal
        isOpen={showCSVModal}
        onClose={() => setShowCSVModal(false)}
        onImport={handleCSVImport}
        brandName={brandName}
      />

      {/* Sites Filters Panel */}
      <SitesFilters
        isOpen={isSitesFilterOpen}
        onClose={() => setIsSitesFilterOpen(false)}
        onApplyFilters={handleApplyFilters}
        availableStates={getAvailableStates(stores)}
        availableCounties={getAvailableCounties(stores)}
        availableCities={getAvailableCities(stores)}
        storeGeoData={stores.map(s => ({ state: s.state ?? '-', county: s.county ?? '-', city: s.city ?? '-' }))}
        isUltimateParent={isUltimateParent}
        availableSubBrands={getAvailableSubBrands(stores)}
      />

      {/* Notes Dialog */}
      <BrandNotesDialog
        isOpen={notesDialogOpen}
        onClose={() => setNotesDialogOpen(false)}
        brandName={selectedStoreForNotes}
        isStoreNote={true}
        poiId={selectedStorePoiId}
        notes={stores.find(s => s.poiId === selectedStorePoiId)?.store_notes}
        onNoteAdded={handleNoteAdded}
      />

      {/* Store Detail Modal */}
      <StoreDetailModal
        isOpen={storeDetailModalOpen}
        store={selectedStoreDetail}
        onClose={() => setStoreDetailModalOpen(false)}
      />

      {/* Add to List Dialog */}
      <AddToListDialog
        isOpen={listDialogOpen}
        onClose={() => { setListDialogOpen(false); loadSavedStores(); }}
        type="store"
        poiId={selectedStoreForList.poiId}
        brandName={selectedStoreForList.brandName}
        storeName={selectedStoreForList.storeName}
        bulkStores={bulkStoresForList.length > 0 ? bulkStoresForList : undefined}
      />

    </div>
  );
}

export default AllSites;